// eslint-disable-next-line jira/restricted/react-component-props
import React, { useMemo, type ComponentProps } from 'react';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import { useRegisterNudgeTarget } from '@atlassiansox/nudge-tooltip';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import BoardKitColumnHeader from '@atlassian/jira-platform-board-kit/src/common/ui/column-header/main.tsx';
import {
	TailorBoardColumnsAsync,
	TailorBoardColumnsAsyncChildless,
} from '@atlassian/jira-software-onboarding-nudges--next/src/ui/jwm-board-view/async.tsx';
import { ConditionalNudgeWrapper } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/conditional-nudge-wrapper/index.tsx';
import { isTailoredOnboardingEnabled } from '@atlassian/jira-tailored-onboarding/src/controllers/index.tsx';
import { TAILOR_BOARD_COLUMN_NUDGE_ID } from '@atlassian/jira-onboarding-core/src/constants.tsx';
import { useSoftwareProjectTheming } from '@atlassian/jira-project-theme-providers/src/controllers/software-theme/index.tsx';
import { getIsSoftwareThemingM2Enabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { Capability } from '../../../../../common/capability/index.tsx';
import { useBoardSelector } from '../../../../../state/index.tsx';
import {
	getIsCMPBoard,
	getIsIncrementPlanningBoard,
	getIsSprintsEnabled,
} from '../../../../../state/selectors/software/software-selectors.tsx';
import { useCapability } from '../../../../../state/state-hooks/capabilities/index.tsx';
import { useHasSwimlanes } from '../../../../../common/hooks/use-has-swimlanes/useHasSwimlanes.tsx';
import { useAgentDetailsFromColumn } from '../../../../../common/hooks/use-agent-details-from-column/index.tsx';
import messages from './messages.tsx';

type BoardKitColumnHeaderProps = JSX.LibraryManagedAttributes<
	typeof BoardKitColumnHeader,
	ComponentProps<typeof BoardKitColumnHeader>
>;

type Props = {
	isDone: boolean;
	isLastColumn: boolean;
	cardsCountIgnoreSwimlane?: number;
	totalCardsCountIgnoreSwimlane?: number;
} & BoardKitColumnHeaderProps;

const ColumnHeaderView = (props: Props) => {
	const { formatMessage } = useIntl();
	const { isDone, isLastColumn, ...otherProps } = props;
	const isCMPBoard = useBoardSelector(getIsCMPBoard);
	const isIncrementPlanningBoard = useBoardSelector(getIsIncrementPlanningBoard);
	const isFlexibleColumns = useCapability(Capability.FLEXIBLE_BOARD_COLUMNS);
	const shouldRemoveSpaceForMenu = isCMPBoard || isIncrementPlanningBoard;
	const hasSwimlanes = useHasSwimlanes();
	const { isSoftwareFullTheming } = getIsSoftwareThemingM2Enabled()
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useSoftwareProjectTheming()
		: { isSoftwareFullTheming: false };

	// Determine if this is a scrum board in a team-managed project
	// Team-managed projects have isCMPBoard = false
	// For scrum team-managed boards, we check if sprints are enabled
	const isSprintsEnabled = useBoardSelector(getIsSprintsEnabled);
	const isTeamManagedProject = !isCMPBoard;
	const isScrumTeamManagedBoard = isTeamManagedProject && isSprintsEnabled;

	/**
	 * Will only be used for Team EU and a more long-term solution will be used for Open Beta
	 * Gets an agentId from the column's first transition if it has a "Trigger Agent" rule
	 */
	const columnIdAsString = props?.id?.toString();
	const { agentDetails } =
		columnIdAsString && fg('jira_ai_enable_assignable_agents_in_boards')
			? // eslint-disable-next-line react-hooks/react-compiler
				// eslint-disable-next-line react-hooks/rules-of-hooks
				useAgentDetailsFromColumn(columnIdAsString)
			: { agentDetails: null };

	const { ref: tailorBoardColumnsNudgeRef } = useRegisterNudgeTarget<HTMLDivElement>(
		TAILOR_BOARD_COLUMN_NUDGE_ID,
		fg('board_nudge_bugfix') && isLastColumn,
	);

	const tooltipContent = useMemo(() => {
		if (!hasSwimlanes || !fg('dee_8389_add_tooltip_to_column_header')) {
			return null;
		}

		if (otherProps.showTotalCardsCount) {
			return `${formatMessage(messages.totalInAllGroups)}: ${otherProps.cardsCountIgnoreSwimlane}/${otherProps.totalCardsCountIgnoreSwimlane}`;
		}

		return `${formatMessage(messages.totalInAllGroups)}: ${otherProps.totalCardsCountIgnoreSwimlane}`;
	}, [
		otherProps.showTotalCardsCount,
		otherProps.cardsCountIgnoreSwimlane,
		otherProps.totalCardsCountIgnoreSwimlane,
		formatMessage,
		hasSwimlanes,
	]);

	const renderBoardKitColumnHeader = (
		columnHeaderWrapperRef?: React.MutableRefObject<HTMLDivElement | null>,
	) => (
		<BoardKitColumnHeader
			columnHeaderWrapperRef={columnHeaderWrapperRef}
			isDone={isDone}
			{...otherProps}
			isCMPBoard={isCMPBoard}
			shouldRemoveSpaceForMenu={shouldRemoveSpaceForMenu}
			isFlexibleColumns={isFlexibleColumns}
			hasSwimlanes={hasSwimlanes}
			isThemed={isSoftwareFullTheming}
			isScrumTeamManagedBoard={isScrumTeamManagedBoard}
			{...(fg('dee_8389_add_tooltip_to_column_header') && {
				tooltipContent,
			})}
			{...(fg('jira_ai_enable_assignable_agents_in_boards') && {
				experimentalAgentAvatarProps: agentDetails
					? {
							agentId: agentDetails.id,
							name: agentDetails.name,
							agentIdentityAccountId: agentDetails.identity_account_id,
							agentNamedId: agentDetails.external_config_reference ?? agentDetails.named_id,
							size: 'small',
							isForgeAgent:
								agentDetails.creator_type === 'FORGE' ||
								agentDetails.creator_type === 'THIRD_PARTY',
							forgeAgentIconUrl: agentDetails.icon,
						}
					: undefined,
			})}
		/>
	);

	if (isLastColumn) {
		if (fg('board_nudge_bugfix')) {
			return (
				<>
					<SpotlightTarget name="DoneNotLastColumnMessage">
						{renderBoardKitColumnHeader(tailorBoardColumnsNudgeRef)}
					</SpotlightTarget>
					<EngagementSpotlight engagementId="DoneNotLastColumnMessage" />
					<TailorBoardColumnsAsyncChildless />
				</>
			);
		}
		return (
			<>
				<ConditionalNudgeWrapper
					conditionsToApplyWrapper={!isCMPBoard && isTailoredOnboardingEnabled()}
					Wrapper={TailorBoardColumnsAsync}
				>
					<SpotlightTarget name="DoneNotLastColumnMessage">
						{renderBoardKitColumnHeader()}
					</SpotlightTarget>
				</ConditionalNudgeWrapper>
				<EngagementSpotlight engagementId="DoneNotLastColumnMessage" />
			</>
		);
	}

	if (isDone) {
		return (
			<>
				<SpotlightTarget name="DoneColumnMessage">{renderBoardKitColumnHeader()}</SpotlightTarget>
				<EngagementSpotlight engagementId="DoneColumnMessage" />
			</>
		);
	}

	return renderBoardKitColumnHeader();
};

export default ColumnHeaderView;
